import { BaseResponse } from '@/types/common';
import axiosInstance from '@/api/axiosInstance';
import dayjs from 'dayjs';
import type {
  NewcomerChallengeListParams,
  NewcomerChallengeListResponse,
  NewcomerChallenge,
  SubmissionArtworkListParams,
  SubmissionArtwork,
} from './types';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * VIP权益接口返回数据
 */
export interface VipRight {
  id: number;
  rightsName: string;
  isEnabled: number;
}

/**
 * 获取VIP权益列表
 */
export const getVipRights = async (): Promise<BaseResponse<VipRight[]>> => {
  const response = await axiosInstance.get<BaseResponse<VipRight[]>>(
    `${baseUrl}/rookieKingChallenge/queryVipRights`
  );
  return response.data;
};

/**
 * 奖励类型接口返回数据
 */
export interface AwardType {
  id: number;
  typeName?: string;
  dayType?: number | null;
  [key: string]: any;
}

/**
 * 获取奖励类型列表
 */
export const getAwardTypes = async (): Promise<BaseResponse<AwardType[]>> => {
  const response = await axiosInstance.get<BaseResponse<AwardType[]>>(
    `${baseUrl}/rookieKingChallenge/queryAwardType`
  );
  return response.data;
};

/**
 * 活动规则简要信息（用于规则说明下拉框）
 */
export interface ActivityRuleOption {
  id: number;
  activityTopicEn?: string;
  activityTopicJa?: string;
}

/**
 * 后端返回的活动规则列表响应
 */
interface BackendActivityRuleListResponse {
  records?: Array<{
    id?: number;
    activityTopicEn?: string;
    activityTopicJa?: string;
    [key: string]: any;
  }>;
  total?: number;
  size?: number;
  current?: number;
  pages?: number;
}

/**
 * 获取活动规则列表（用于规则说明下拉框）
 */
export const getActivityRuleOptions = async (): Promise<BaseResponse<ActivityRuleOption[]>> => {
  // 获取所有活动规则，不分页或使用较大的pageSize
  const response = await axiosInstance.get<BaseResponse<BackendActivityRuleListResponse>>(
    `${baseUrl}/ActivityRule/page`,
    {
      params: {
        pageNum: 1,
        pageSize: 9999, // 获取足够多的数据
      },
    }
  );

  if (response.data && response.data.data && response.data.data.records) {
    const records = response.data.data.records.map((item) => ({
      id: item.id!,
      activityTopicEn: item.activityTopicEn,
      activityTopicJa: item.activityTopicJa,
    }));

    return {
      ...response.data,
      data: records,
    };
  }

  return {
    status: response.data?.status || 200,
    message: response.data?.message || '获取活动规则列表失败',
    data: [],
  };
};

/**
 * 获取新人王挑战赛列表
 */
export const getNewcomerChallengeList = async (
  params: NewcomerChallengeListParams
): Promise<BaseResponse<NewcomerChallengeListResponse>> => {
  // 构建请求参数
  const requestParams: any = {};
  if (params.id !== undefined) {
    requestParams.id = params.id;
  }
  if (params.title) {
    requestParams.title = params.title;
  }
  if (params.updateUserName) {
    requestParams.userName = params.updateUserName;
  }
  if (params.status !== undefined) {
    requestParams.status = params.status;
  }
  if (params.currentPage !== undefined) {
    requestParams.currentPage = params.currentPage;
  }
  if (params.pageSize !== undefined) {
    requestParams.pageSize = params.pageSize;
  }

  const response = await axiosInstance.post<BaseResponse<any>>(
    `${baseUrl}/rookieKingChallenge/list`,
    requestParams
  );

  // 转换后端数据格式为前端需要的格式
  if (response.data && response.data.status === 200 && response.data.data) {
    const backendData = response.data.data;
    // 后端可能返回的数据结构：{ records: [], total: number, currentPage: number, pageSize: number }
    // 或者：{ list: [], total: number, current: number, size: number }
    const records = backendData.records || backendData.list || [];
    const total = backendData.total || 0;
    const currentPage = backendData.currentPage || backendData.current || params.currentPage || 1;
    const pageSize = backendData.pageSize || backendData.size || params.pageSize || 10;

    return {
      ...response.data,
      data: {
        total,
        currentPage,
        pageSize,
        records: records.map((item: any) => ({
          id: item.id,
          title: item.title,
          subtitle: item.subtitle,
          status: item.status,
          updateUserName: item.updateUserName || item.userName,
          // 时间字段：ISO 字符串转换为时间戳
          submitBeginTime: item.submitStartTime 
            ? (typeof item.submitStartTime === 'string' 
                ? dayjs(item.submitStartTime).valueOf() 
                : Number(item.submitStartTime))
            : undefined,
          submitEndTime: item.submitEndTime 
            ? (typeof item.submitEndTime === 'string' 
                ? dayjs(item.submitEndTime).valueOf() 
                : Number(item.submitEndTime))
            : undefined,
          voteBeginTime: item.voteStartTime 
            ? (typeof item.voteStartTime === 'string' 
                ? dayjs(item.voteStartTime).valueOf() 
                : Number(item.voteStartTime))
            : undefined,
          voteEndTime: item.voteEndTime 
            ? (typeof item.voteEndTime === 'string' 
                ? dayjs(item.voteEndTime).valueOf() 
                : Number(item.voteEndTime))
            : undefined,
          eventDescription: item.eventDescription,
          otherRewardDesc: item.otherRewardDesc,
          ruleId: item.ruleConfigId,
          voteWeight: item.voteWeight ? Number(item.voteWeight) : undefined,
          likeWeight: item.likeWeight ? Number(item.likeWeight) : undefined,
          collectWeight: item.collectWeight ? Number(item.collectWeight) : undefined,
          commentWeight: item.commentWeight ? Number(item.commentWeight) : undefined,
          // 图片字段映射
          rookieKingCoverJa: item.coverUrlJp,
          rookieKingCover: item.coverUrlEn,
          previousCoverJa: item.previousCoverUrlJp,
          previousCover: item.previousCoverUrlEn,
          previousListJa: item.previousListUrlJp,
          previousList: item.previousListUrlEn,
          detailBgJa: item.detailBgUrlJp,
          detailBg: item.detailBgUrlEn,
          // 奖励列表：rewardSubList -> rewards（保留原始数据用于列表显示）
          rewardSubList: item.rewardSubList || [],
          rewards: item.rewardSubList?.map((reward: any) => {
            // 注意：这里保留 rank 字段用于兼容，但实际应该使用 type
            // typeName 会在列表渲染时通过 awardTypes 获取
            return {
              type: reward.type,
              num: reward.winnerCount,
              purpleCoins: reward.purpleCoin,
              cash: reward.cashAmount,
              vipBenefits: reward.vipRightsId,
              dayType: reward.dayType,
            };
          }) || [],
          createTime: item.createTime,
          rewardConfigWinnerCount: item.rewardConfigWinnerCount,
          rewardWinnerCount: item.rewardWinnerCount,
        })),
      },
    };
  }

  return {
    status: response.data?.status || 200,
    message: response.data?.message || '获取列表失败',
    data: {
      total: 0,
      currentPage: params.currentPage || 1,
      pageSize: params.pageSize || 10,
      records: [],
    },
  };
};

/**
 * 获取新人王挑战赛详情（从列表接口获取）
 */
export const getNewcomerChallengeDetail = async (
  id: number
): Promise<BaseResponse<NewcomerChallenge>> => {
  // 使用列表接口获取详情，通过 id 查询
  const listResponse = await getNewcomerChallengeList({ id, currentPage: 1, pageSize: 1 });
  
  if (listResponse.data && listResponse.data.records && listResponse.data.records.length > 0) {
    return {
      status: listResponse.status,
      message: listResponse.message,
      data: listResponse.data.records[0],
    };
  }

  return {
    status: 404,
    message: '未找到该活动',
    data: {} as NewcomerChallenge,
  };
};

/**
 * 创建新人王挑战赛
 */
export const createNewcomerChallenge = async (
  data: NewcomerChallenge
): Promise<BaseResponse<NewcomerChallenge>> => {
  // 转换前端数据格式为后端需要的格式
  const requestData: any = {
    title: data.title,
    subtitle: data.subtitle,
    ruleConfigId: data.ruleId,
    otherRewardDesc: data.otherRewardDesc,
    voteWeight: data.voteWeight?.toString(),
    likeWeight: data.likeWeight?.toString(),
    collectWeight: data.collectWeight?.toString(),
    commentWeight: data.commentWeight?.toString(),
    status: 0,
    // 图片字段映射
    // coverUrlJp 和 coverUrlEn 对应 Rookie King封面图
    coverUrlJp: data.rookieKingCoverJa,
    coverUrlEn: data.rookieKingCover,
    previousCoverUrlJp: data.previousCoverJa,
    previousCoverUrlEn: data.previousCover,
    previousListUrlJp: data.previousListJa,
    previousListUrlEn: data.previousList,
    detailBgUrlJp: data.detailBgJa,
    detailBgUrlEn: data.detailBg,
  };

  // 日期字段：直接传递时间戳
  if (data.submitBeginTime !== undefined) {
    requestData.submitStartTime = data.submitBeginTime;
  }
  if (data.submitEndTime !== undefined) {
    requestData.submitEndTime = data.submitEndTime;
  }
  if (data.voteBeginTime !== undefined) {
    requestData.voteStartTime = data.voteBeginTime;
  }
  if (data.voteEndTime !== undefined) {
    requestData.voteEndTime = data.voteEndTime;
  }

  // 奖励列表：rewards -> rewardSubList
  if (data.rewards && data.rewards.length > 0) {
    requestData.rewardSubList = data.rewards
      .filter((reward) => reward.type !== undefined)
      .map((reward) => ({
        type: reward.type!, // 使用奖励类型ID（从接口获取的id）
        winnerCount: reward.num, // 获奖人数
        purpleCoin: reward.purpleCoins, // 活动金币数量
        cashAmount: reward.cash, // 现金数量
        vipRightsId: reward.vipBenefits, // VIP权益ID
        dayType: reward.dayType, // 天数类型（3-3日奖励，7-7日奖励）
      }));
  }

  const response = await axiosInstance.post<BaseResponse<NewcomerChallenge>>(
    `${baseUrl}/rookieKingChallenge/add`,
    requestData
  );

  return response.data;
};

/**
 * 更新新人王挑战赛
 */
export const updateNewcomerChallenge = async (
  data: NewcomerChallenge
): Promise<BaseResponse<NewcomerChallenge>> => {
  // 转换前端数据格式为后端需要的格式（和新增接口一致，但必须包含 id）
  const requestData: any = {
    id: data.id, // 必须包含当前 id
    title: data.title,
    subtitle: data.subtitle,
    ruleConfigId: data.ruleId,
    otherRewardDesc: data.otherRewardDesc,
    voteWeight: data.voteWeight?.toString(),
    likeWeight: data.likeWeight?.toString(),
    collectWeight: data.collectWeight?.toString(),
    commentWeight: data.commentWeight?.toString(),
    // 图片字段映射
    coverUrlJp: data.rookieKingCoverJa,
    coverUrlEn: data.rookieKingCover,
    previousCoverUrlJp: data.previousCoverJa,
    previousCoverUrlEn: data.previousCover,
    previousListUrlJp: data.previousListJa,
    previousListUrlEn: data.previousList,
    detailBgUrlJp: data.detailBgJa,
    detailBgUrlEn: data.detailBg,
  };

  // 日期字段：直接传递时间戳
  if (data.submitBeginTime !== undefined) {
    requestData.submitStartTime = data.submitBeginTime;
  }
  if (data.submitEndTime !== undefined) {
    requestData.submitEndTime = data.submitEndTime;
  }
  if (data.voteBeginTime !== undefined) {
    requestData.voteStartTime = data.voteBeginTime;
  }
  if (data.voteEndTime !== undefined) {
    requestData.voteEndTime = data.voteEndTime;
  }

  // 奖励列表：rewards -> rewardSubList
  if (data.rewards && data.rewards.length > 0) {
    requestData.rewardSubList = data.rewards
      .filter((reward) => reward.type !== undefined)
      .map((reward) => ({
        type: reward.type!, // 使用奖励类型ID（从接口获取的id）
        winnerCount: reward.num, // 获奖人数
        purpleCoin: reward.purpleCoins, // 活动金币数量
        cashAmount: reward.cash, // 现金数量
        vipRightsId: reward.vipBenefits, // VIP权益ID
        dayType: reward.dayType, // 天数类型（3-3日奖励，7-7日奖励）
      }));
  }

  const response = await axiosInstance.post<BaseResponse<NewcomerChallenge>>(
    `${baseUrl}/rookieKingChallenge/update`,
    requestData
  );

  return response.data;
};

/**
 * 删除新人王挑战赛（Mock）
 */
/**
 * 删除新人王挑战赛
 */
export const deleteNewcomerChallenge = async (
  id: number
): Promise<BaseResponse<void>> => {
  const response = await axiosInstance.post<BaseResponse<void>>(
    `${baseUrl}/rookieKingChallenge/delete/${id}`
  );
  return response.data;
};

/**
 * 奖励类型映射：数字 -> 名称
 */
const rewardTypeMap: Record<number, string> = {
  0: '新人王',
  1: '创作星',
  2: '绘新锐',
  3: '新人黑马',
  4: '参与激励奖',
  5: '激励奖',
};

/**
 * 后端返回的投稿作品数据格式
 */
interface BackendSubmissionArtwork {
  artworkId?: string;
  artworkImageUrl?: string;
  artworkTitle?: string;
  userName?: string;
  userSuppId?: string;
  vipName?: string;
  hotScore?: number;
  voteNum?: number;
  likeNum?: number;
  collectNum?: number;
  commentNum?: number;
  threeDayRanking?: number;
  threeDayRewardType?: number;
  sevenDayRanking?: number;
  sevenDayRewardType?: number;
  status?: number;
  [key: string]: any;
}

/**
 * 后端返回的投稿作品列表响应格式
 */
interface BackendSubmissionArtworkListResponse {
  records?: BackendSubmissionArtwork[];
  total?: number;
  currentPage?: number;
  pageSize?: number;
  size?: number;
  current?: number;
  pages?: number;
}

/**
 * 获取投稿作品列表
 */
export const getSubmissionArtworkList = async (
  params: SubmissionArtworkListParams
): Promise<BaseResponse<{ total: number; currentPage: number; pageSize: number; records: SubmissionArtwork[] }>> => {
  // 构建请求参数
  const requestParams: any = {};
  if (params.rookieKingChallengeId !== undefined) {
    requestParams.rookieKingChallengeId = String(params.rookieKingChallengeId);
  }
  if (params.artworkTitle) {
    requestParams.artworkTitle = params.artworkTitle;
  }
  if (params.userName) {
    requestParams.userName = params.userName;
  }
  if (params.userSuppId) {
    requestParams.userSuppId = params.userSuppId;
  }
  if (params.vipName) {
    requestParams.vipName = params.vipName;
  }
  if (params.status !== undefined) {
    requestParams.status = params.status;
  }
  if (params.currentPage !== undefined) {
    requestParams.currentPage = params.currentPage;
  }
  if (params.pageSize !== undefined) {
    requestParams.pageSize = params.pageSize;
  }
  if (params.threeDayRewardType !== undefined) {
    requestParams.threeDayRewardType = params.threeDayRewardType;
  }
  if (params.sevenDayRewardType !== undefined) {
    requestParams.sevenDayRewardType = params.sevenDayRewardType;
  }

  const response = await axiosInstance.post<BaseResponse<BackendSubmissionArtworkListResponse>>(
    `${baseUrl}/rookieKingChallenge/artworkPageList`,
    requestParams
  );

  // 转换后端数据格式为前端需要的格式
  if (response.data && response.data.data) {
    const backendData = response.data.data;
    const records = backendData.records || [];
    const total = backendData.total || 0;
    const currentPage = backendData.currentPage || backendData.current || params.currentPage || 1;
    const pageSize = backendData.pageSize || backendData.size || params.pageSize || 10;

    return {
      ...response.data,
      data: {
        total,
        currentPage,
        pageSize,
        records: records.map((item: BackendSubmissionArtwork) => ({
          artworkId: item.artworkId,
          artworkImageUrl: item.artworkImageUrl,
          artworkTitle: item.artworkTitle,
          userName: item.userName,
          userSuppId: item.userSuppId,
          vipName: item.vipName,
          hotScore: item.hotScore,
          voteNum: item.voteNum,
          likeNum: item.likeNum,
          collectNum: item.collectNum,
          commentNum: item.commentNum,
          threeDayRanking: item.threeDayRanking,
          threeDayRewardType: item.threeDayRewardType !== undefined && item.threeDayRewardType !== null
            ? rewardTypeMap[item.threeDayRewardType] || `类型${item.threeDayRewardType}`
            : undefined,
          sevenDayRanking: item.sevenDayRanking,
          sevenDayRewardType: item.sevenDayRewardType !== undefined && item.sevenDayRewardType !== null
            ? rewardTypeMap[item.sevenDayRewardType] || `类型${item.sevenDayRewardType}`
            : undefined,
        })),
      },
    };
  }

  return {
    status: response.data?.status || 200,
    message: response.data?.message || '获取列表失败',
    data: {
      total: 0,
      currentPage: params.currentPage || 1,
      pageSize: params.pageSize || 10,
      records: [],
    },
  };
};

